import { useEffect, useRef, useMemo, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../store";
import { useGetABOEnrichmentDataMutation } from "../../../services/AboEnrichment/AboEnrichmentAPI";
import { addFetchedAbos, markAbosFailed } from "../../../reducers/aboEnrichment";
import { isChinaAff, normalizeEnrichedData } from "../common/ActionReportListView/utils/actionReportUtils";
import type { FieldConfig } from "../common/ActionReportListView/types";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import type { AboKpiData } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";

interface UseAutoEnrichAllProps {
  reportId: string;
  period: string;
  reportData: { aff: string; abo: string }[];
  fieldConfig: FieldConfig[];
  locale: string;
  isoCountryCode: string;
  batchSize?: number;
}

/**
 * Progressively enriches all ABOs in the report by fetching in batches.
 * Reads fetch state directly from Redux to avoid conflicts with row-level lazy enrichment.
 * Returns whether all enrichable ABOs have been fetched (or failed).
 */
export const useAutoEnrichAll = ({
  reportId,
  period,
  reportData,
  fieldConfig,
  locale,
  isoCountryCode,
  batchSize = 10,
}: UseAutoEnrichAllProps) => {
  const dispatch = useAppDispatch();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);

  const [fetchEnrichmentData] = useGetABOEnrichmentDataMutation();

  const kpisToFetch = useMemo(
    () =>
      fieldConfig
        .filter((field) => !field.excludeFromEnrichment)
        .map((field) => (field.id === KPI_ID.PERSONAL_PV_REQ ? KPI_ID.PERSONAL_PV_REQ_AR : field.id)),
    [fieldConfig],
  );

  const enrichmentState = useAppSelector((state) => state.aboEnrichment[reportId]?.[period]);
  const fetchedSet = useMemo(() => new Set(enrichmentState?.affAbosFetched ?? []), [enrichmentState?.affAbosFetched]);
  const failedSet = useMemo(() => new Set(enrichmentState?.affAbosFailed ?? []), [enrichmentState?.affAbosFailed]);

  const allAffAboIds = useMemo(
    () => reportData.filter((row) => !isChinaAff(row.aff)).map((row) => `${row.aff}-${row.abo}`),
    [reportData],
  );

  const unfetchedIds = useMemo(
    () => allAffAboIds.filter((id) => !fetchedSet.has(id) && !failedSet.has(id)),
    [allAffAboIds, fetchedSet, failedSet],
  );

  const isAllEnriched = unfetchedIds.length === 0;

  const fetchNextBatch = useCallback(async () => {
    if (isFetchingRef.current) return;

    const batch = unfetchedIds.slice(0, batchSize);
    if (batch.length === 0) return;

    isFetchingRef.current = true;
    try {
      const response = await fetchEnrichmentData({
        affAbos: batch,
        bonusPeriod: period,
        locale,
        isoCountryCode,
        kpis: kpisToFetch,
      }).unwrap();

      if (isMountedRef.current) {
        const enrichedData = normalizeEnrichedData(response.aboKpiData as AboKpiData[]);
        dispatch(
          addFetchedAbos({
            reportId,
            period,
            affAbos: batch,
            enrichedData,
          }),
        );
      }
    } catch {
      if (isMountedRef.current) {
        dispatch(
          markAbosFailed({
            reportId,
            period,
            affAbos: batch,
          }),
        );
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [unfetchedIds, batchSize, fetchEnrichmentData, period, locale, isoCountryCode, kpisToFetch, dispatch, reportId]);

  // Track mount state to avoid dispatching after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (unfetchedIds.length === 0 || allAffAboIds.length === 0) return;

    timerRef.current = setTimeout(() => {
      fetchNextBatch();
    }, 200);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [unfetchedIds.length, allAffAboIds.length, fetchNextBatch]);

  return { isAllEnriched };
};
